// Reusable ECharts wrapper for the v2 dashboard. Uses the tree-shaken `echarts/core` build so
// only the chart types / components we register are bundled (perf is a priority). The shared
// 'ringil' theme (token-mapped) is registered once here. Built-in dataZoom + brush are
// registered so sections can offer region/coordinate zoom and multi-bar selection without
// pulling extra deps.
import React, { useMemo } from 'react';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { LineChart, BarChart } from 'echarts/charts';
import {
  GridComponent,
  TooltipComponent,
  LegendComponent,
  DataZoomComponent,
  DataZoomInsideComponent,
  BrushComponent,
  ToolboxComponent,
  MarkLineComponent,
  MarkAreaComponent,
} from 'echarts/components';
import { LabelLayout } from 'echarts/features';
import { CanvasRenderer } from 'echarts/renderers';
import type { EChartsCoreOption } from 'echarts/core';
import { ringilChartTheme, RINGIL_CHART_THEME_NAME } from './chartTheme';
import DashboardEmptyState from '../components/DashboardEmptyState.tsx';

echarts.use([
  LineChart,
  BarChart,
  GridComponent,
  TooltipComponent,
  LegendComponent,
  DataZoomComponent,
  DataZoomInsideComponent,
  BrushComponent,
  ToolboxComponent,
  MarkLineComponent,
  MarkAreaComponent,
  LabelLayout,
  CanvasRenderer,
]);

echarts.registerTheme(RINGIL_CHART_THEME_NAME, ringilChartTheme);

interface RingilChartProps {
  option: EChartsCoreOption;
  height?: number | string;
  className?: string;
  loading?: boolean;
  // Map of echarts event name → handler, e.g. { datazoom: fn, brushselected: fn, click: fn }.
  onEvents?: Record<string, (params: unknown) => void>;
  // When the option fully replaces the previous one (e.g. switching range), pass true.
  notMerge?: boolean;
  // When the selected period has no data (or all values are zero), show the shared empty-state
  // placeholder instead of rendering empty axes. Optional custom message via emptyLabel.
  isEmpty?: boolean;
  emptyLabel?: string;
}

const RingilChart: React.FC<RingilChartProps> = ({ option, height = 280, className, loading, onEvents, notMerge, isEmpty, emptyLabel }) => {
  const style = useMemo(() => ({ height: typeof height === 'number' ? `${height}px` : height, width: '100%' }), [height]);

  if (isEmpty) {
    return <DashboardEmptyState height={height} label={emptyLabel} />;
  }

  return (
    <ReactEChartsCore
      echarts={echarts}
      option={option}
      theme={RINGIL_CHART_THEME_NAME}
      notMerge={notMerge}
      lazyUpdate
      showLoading={loading}
      style={style}
      className={className}
      onEvents={onEvents}
      opts={{ renderer: 'canvas' }}
    />
  );
};

export default RingilChart;
